// frontend/src/components/PrimeiroAcesso.jsx
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../config/api';
import { useModal } from '../contexts/ModalContext'; // importa o hook do modal global
import { useAuth } from '../contexts/AuthContext';

const PrimeiroAcesso = ({ onSenhaAlterada }) => {
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const { showModal } = useModal(); // usa modal global
  const { checkAuth } = useAuth();

  const validarSenha = () => {
    if (novaSenha.length < 6) {
      setErro('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (novaSenha !== confirmarSenha) {
      setErro('As senhas não coincidem');
      return false;
    }

    const temNumero = /\d/.test(novaSenha);
    const temLetra = /[a-zA-Z]/.test(novaSenha);

    if (!temNumero || !temLetra) {
      setErro('A senha deve conter letras e números');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    if (!validarSenha()) return;

    setLoading(true);

    try {
      const response = await api.post('/auth/primeiro-acesso/alterar-senha', { novaSenha });

      if (response.success) {
        // ✔️ Modal global substituindo o alert()
        showModal({
          type: 'success',
          title: 'Senha alterada com sucesso!',
          message: 'Sua senha foi atualizada. Clique em continuar para seguir.',
          confirmText: 'Continuar',
          showCancel: false,
          onConfirm: async () => {
            try {
              await checkAuth();
            } catch {
              // ignore
            }
            if (typeof onSenhaAlterada === 'function') {
              onSenhaAlterada();
            } else {
              window.location.href = '/';
            }
          }
        });
      } else {
        setErro(response.message || 'Erro ao alterar senha');
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setErro(error.message || 'Erro ao alterar senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getForcaSenha = () => {
    if (novaSenha.length === 0) return { texto: '', cor: '', porcentagem: 0 };
    if (novaSenha.length < 6) return { texto: 'Fraca', cor: 'bg-red-500', porcentagem: 25 };

    const temNumero = /\d/.test(novaSenha);
    const temLetra = /[a-zA-Z]/.test(novaSenha);
    const temMaiuscula = /[A-Z]/.test(novaSenha);
    const temEspecial = /[!@#$%^&*(),.?":{}|<>]/.test(novaSenha);

    let forca = 0;
    if (temLetra) forca++;
    if (temNumero) forca++;
    if (temMaiuscula) forca++;
    if (temEspecial) forca++;
    if (novaSenha.length >= 8) forca++;

    if (forca <= 2) return { texto: 'Média', cor: 'bg-yellow-500', porcentagem: 50 };
    if (forca <= 3) return { texto: 'Boa', cor: 'bg-blue-500', porcentagem: 75 };
    return { texto: 'Forte', cor: 'bg-green-500', porcentagem: 100 };
  };

  const forcaSenha = getForcaSenha();

  return (
    <>
    {/* Fundo gradiente fixo que cobre toda viewport */}
    <div className="auth-bg" />
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md">
        
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          
          {/* Top Bar */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 rounded-xl flex items-center gap-3 mb-6">
            <Lock size={20} className="text-white" />
            <h2 className="text-lg font-bold text-white">
              Primeiro Acesso — Alterar Senha
            </h2>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Nova Senha */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nova Senha</label>
              <div className="relative">
                <input
                  type={mostrarNovaSenha ? 'text' : 'password'}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  className="w-full px-3 pr-10 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 text-sm"
                  placeholder="Digite sua nova senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setMostrarNovaSenha(!mostrarNovaSenha)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {mostrarNovaSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Força */}
              {novaSenha && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600">Força da senha:</span>
                    <span
                      className={`font-semibold ${
                        forcaSenha.texto === 'Fraca'
                          ? 'text-red-600'
                          : forcaSenha.texto === 'Média'
                          ? 'text-yellow-600'
                          : forcaSenha.texto === 'Boa'
                          ? 'text-blue-600'
                          : 'text-green-600'
                      }`}
                    >
                      {forcaSenha.texto}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${forcaSenha.cor} transition-all duration-300`}
                      style={{ width: `${forcaSenha.porcentagem}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirmar Senha */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <input
                  type={mostrarConfirmar ? 'text' : 'password'}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="w-full px-3 pr-10 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 text-sm"
                  placeholder="Digite novamente sua nova senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {mostrarConfirmar ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Match */}
              {confirmarSenha && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {novaSenha === confirmarSenha ? (
                    <>
                      <CheckCircle size={14} className="text-green-600" />
                      <span className="text-green-600 font-semibold">
                        As senhas coincidem
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={14} className="text-red-600" />
                      <span className="text-red-600 font-semibold">
                        As senhas não coincidem
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Requisitos */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
              <p className="font-semibold text-blue-900 mb-2">Requisitos da senha:</p>
              <ul className="text-blue-800 space-y-1">
                <li className="flex items-center gap-2">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      novaSenha.length >= 6 ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  Mínimo de 6 caracteres
                </li>
                <li className="flex items-center gap-2">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      /[a-zA-Z]/.test(novaSenha) ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  Pelo menos uma letra
                </li>
                <li className="flex items-center gap-2">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      /\d/.test(novaSenha) ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  Pelo menos um número
                </li>
              </ul>
            </div>

            {/* Erro */}
            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle size={18} className="text-red-600 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{erro}</p>
              </div>
            )}

            {/* Botão */}
            <button
              type="submit"
              disabled={loading || !novaSenha || !confirmarSenha}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold py-3 rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Alterando senha...
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Confirmar Nova Senha
                </>
              )}
            </button>
          </form>

          {/* Aviso */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
            <p className="text-amber-800 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5" />
              Esta é uma medida de segurança obrigatória. Escolha uma senha forte que você consiga lembrar.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white text-xs">
            © {new Date().getFullYear()} Inovacom Software - Todos os direitos reservados
          </p>
        </div>

      </div>
    </div>
    </>
  );
};

export default PrimeiroAcesso;
